import { useEffect, useRef, useState } from 'react';
import { DIFFICULTY_MAX } from '../../utils/difficultyLevels';

interface DifficultyIndicatorProps {
  /** Current persisted level (null when unrated). Renders as "N/A" when null. */
  level: number | null;
  /**
   * Composite key driving React-tree remount per FR-012a "Highlight hygiene
   * across navigation". Construct as `${tableResultId}-${assignSeq}` so the
   * span unmounts + remounts on (a) hand change and (b) successful Assign.
   * The remount cancels any in-flight CSS animation on the old node.
   */
  flashKey: string;
  /** True only when the most recent render is a post-Assign render (drives the highlight). */
  flashRequested: boolean;
}

/**
 * Reads OS-level prefers-reduced-motion (FR-012b). Listens to media-query change
 * events so an Editor who toggles the OS preference mid-session is respected
 * immediately.
 */
function usePrefersReducedMotion(): boolean {
  const [reduce, setReduce] = useState<boolean>(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia('(prefers-reduced-motion: reduce)');
    const listener = (e: MediaQueryListEvent) => setReduce(e.matches);
    mql.addEventListener('change', listener);
    return () => mql.removeEventListener('change', listener);
  }, []);

  return reduce;
}

const REDUCED_MOTION_HOLD_MS = 1500;

// "Difficulty: <level>/<max>" indicator beside the "From" section header strip
// on the Overcalling Assign Level of Difficulty page (FR-012). Renders "N/A"
// when unrated. On Assign, the value cell gets a background-highlight fade per
// FR-012a; under prefers-reduced-motion the fade is replaced with an instant
// appear / instant disappear (FR-012b). Cancellation on navigation is handled
// by the React-key remount pattern.
// Introduced by feature 012-overcalling-assign-difficulty.
export function DifficultyIndicator({ level, flashKey, flashRequested }: DifficultyIndicatorProps) {
  const reduceMotion = usePrefersReducedMotion();
  const [showReducedHighlight, setShowReducedHighlight] = useState(false);
  const timeoutRef = useRef<number | null>(null);

  useEffect(() => {
    // Only the reduced-motion branch needs JS-driven cleanup; the animated branch
    // is purely CSS and is cancelled by the React-key remount.
    if (!flashRequested || !reduceMotion) {
      setShowReducedHighlight(false);
      return;
    }
    setShowReducedHighlight(true);
    timeoutRef.current = window.setTimeout(() => {
      setShowReducedHighlight(false);
      timeoutRef.current = null;
    }, REDUCED_MOTION_HOLD_MS);

    return () => {
      if (timeoutRef.current !== null) {
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
    // flashKey participates so a fresh Assign on the same hand restarts the timer.
  }, [flashKey, flashRequested, reduceMotion]);

  const displayValue = level === null ? 'N/A' : `${level}/${DIFFICULTY_MAX}`;

  // Animated branch: apply the CSS keyframe class. The reduced-motion branch
  // uses an inline style so it doesn't depend on the page CSS.
  const valueClassName =
    flashRequested && !reduceMotion ? 'animate-difficulty-flash' : undefined;
  const valueStyle: React.CSSProperties | undefined =
    flashRequested && reduceMotion && showReducedHighlight
      ? { backgroundColor: '#fbbf24' }
      : undefined;

  return (
    <div className="flex items-center gap-1.5" data-testid="difficulty-indicator">
      <span className="text-sm font-medium text-ink">Difficulty:</span>
      <span
        key={flashKey}
        data-testid="difficulty-indicator-value"
        className={`text-sm font-semibold px-1 rounded-sm ${valueClassName ?? ''}`.trim()}
        style={valueStyle}
      >
        {displayValue}
      </span>
    </div>
  );
}
